import './music.css';
import type { MusicAction } from '../net/protocol';
import { t, tText, onLang } from './i18n';

/**
 * Shared YouTube playlist panel built on the official IFrame Player API. The
 * room manages one queue together: search and add tracks, delete, reorder, and
 * pick a track; play / pause / seek / volume and every queue edit are broadcast
 * over the game WebSocket so all clients stay in step — including play/pause/
 * seek/volume done from *inside* the YouTube player, detected via state events
 * and a light poll (the IFrame API fires no event for scrub/volume drags).
 *
 * Tracks are added through the in-panel search box, which needs a YouTube Data
 * API v3 key (VITE_YOUTUBE_API_KEY); search results show a thumbnail. The
 * synced queue itself needs no API key.
 *
 * Minimized, the panel is a compact transport bar (prev/play/pause/next +
 * now-playing). Expanding it reveals the video player, search, and queue editor
 * and hides the transport bar — you drive playback from the embedded player.
 */

interface YTPlayer {
  playVideo(): void;
  pauseVideo(): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  loadVideoById(id: string, startSeconds?: number): void;
  setVolume(v: number): void;
  getVolume(): number;
  getCurrentTime(): number;
  getVideoData(): { title?: string; video_id?: string };
}

interface YTNamespace {
  Player: new (
    el: HTMLElement,
    opts: {
      height?: string | number;
      width?: string | number;
      playerVars?: Record<string, number | string>;
      events?: { onReady?: () => void; onStateChange?: (e: { data: number }) => void };
    }
  ) => YTPlayer;
  PlayerState: { ENDED: number; PLAYING: number; PAUSED: number };
}

declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

const IFRAME_API_SRC = 'https://www.youtube.com/iframe_api';

interface Track {
  id: string;
  title: string;
}

/** Default queue: trending K-pop (resolved to embeddable YouTube IDs). The room
 *  host's queue syncs to everyone, so this is just a friendly starting playlist.
 *  A few extra tracks are held back as store-purchasable unlocks (kind:'music'
 *  in src/store/catalog.ts) — added via `addOwnedTrack` once bought. */
const DEFAULT_PLAYLIST: Track[] = [
  { id: 'HtJS32n6LNQ', title: 'TVXQ — Mirotic' },
  { id: '2Cv3phvP8Ro', title: 'BIGBANG — Lies (거짓말)' },
  { id: 'MzCbEdtNbJ0', title: 'BIGBANG — Haru Haru' },
  { id: 'BlHv3BbBv6A', title: 'Wonder Girls — Tell Me' },
  { id: 'BA7fdSkp8ds', title: 'Wonder Girls — Nobody' },
  { id: 'U7mPqycQ0tQ', title: "Girls' Generation — Gee" },
  { id: '6SwiSpudKWI', title: "Girls' Generation — Genie" },
  { id: 'x6QA3m58DQw', title: 'Super Junior — Sorry Sorry' },
  { id: 'roughtzsCDI', title: 'SHINee — Ring Ding Dong' },
  { id: '4MgAxMO1KD0', title: "2NE1 — I Don't Care" },
  { id: '49AfuuRbgGo', title: '2NE1 — Fire' },
  { id: 's2EQm6WPMHs', title: 'KARA — Mister' },
  { id: 'ofwFr8o8p0Y', title: 'Brown Eyed Girls — Abracadabra' },
  { id: 'j492N1OX47I', title: "Rain — It's Raining" },
  { id: 'mR8i0fRVmSA', title: 'BoA — My Name' },
  { id: 'iKdr44yEBQU', title: 'Lee Hyori — 10 Minutes' },
  { id: '9403-9CptH8', title: 'T-ara — Bo Peep Bo Peep' },
];

interface SearchResult extends Track {
  thumb?: string;
}

/** Parse a YouTube URL or bare ID into an 11-char video ID (or null). */
export function parseVideoId(input: string): string | null {
  const s = input.trim();
  if (!s) return null;
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return s;
  const m = s.match(/(?:youtu\.be\/|v=|\/embed\/|\/shorts\/|\/v\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}

/** Pull a playlist ID out of a `…&list=PL…` URL (or null). */
export function parsePlaylistId(input: string): string | null {
  const m = input.match(/[?&]list=([A-Za-z0-9_-]+)/);
  return m ? m[1] : null;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
}

export class MusicPanel {
  private readonly panel = el('div', 'hud-panel interactive');
  private readonly editor = el('div', 'music-body'); // collapsible queue editor
  private readonly embed = el('div', 'music-embed');
  private readonly searchInput = el('input', 'music-input');
  private readonly searchResults = el('div', 'music-results');
  private readonly list = el('div', 'music-list');
  private readonly status = el('div', 'music-status');
  private readonly syncBtn = el('button', 'music-sync');
  private readonly nowPlaying = el('div', 'music-now');
  private readonly volume = el('input', 'music-volume');
  private readonly transport = el('div', 'music-row'); // compact playback bar (minimized only)
  private readonly resizeGrip = el('div', 'music-resize'); // bottom-right drag-to-resize corner
  private open = false; // queue editor / video expanded?

  /** YouTube Data API key (search / playlist expansion). Empty → search hidden. */
  private readonly apiKey: string = (import.meta.env.VITE_YOUTUBE_API_KEY as string | undefined) ?? '';

  private api: YTNamespace | null = null;
  private player: YTPlayer | null = null;
  private playerReady = false;
  private queued: (() => void)[] = [];

  private playlist: Track[] = DEFAULT_PLAYLIST.slice();
  private readonly ppBtn = el('button', 'music-pp'); // single play/pause toggle
  private index = -1;
  private dragFrom = -1; // playlist row being dragged (for reorder)
  private vol = 60;
  private playing = false;
  private online = false;

  // ---- in-player change detection (so play/pause/seek/volume done *inside* the
  // YouTube iframe are broadcast too, not only our own transport buttons) ----
  /** Until this timestamp, the player is mid programmatic change — don't echo it. */
  private suppressUntil = 0;
  private lastTime = 0; // last sampled getCurrentTime(), seconds
  private lastTimeAt = 0; // wall-clock ms when lastTime was sampled
  private monitorTimer: number | null = null;

  constructor(root: HTMLElement, private readonly onAction: (a: MusicAction) => void) {
    this.panel.id = 'music-panel';
    tText(this.syncBtn, 'music.sync');

    // header: title + collapse toggle for the queue editor
    const header = el('div', 'music-header');
    const title = el('span', 'music-title');
    tText(title, 'music.title');
    const collapse = el('button', 'music-collapse', '+');
    // collapsed (minimized) = compact: the transport bar + now-playing only.
    // expanded = full: the video player, search, queue editor — but no transport
    // bar (you drive playback from the embedded player itself).
    this.editor.style.display = 'none';
    collapse.onclick = () => {
      this.open = !this.open;
      collapse.textContent = this.open ? '–' : '+';
      this.applyCollapse();
    };
    header.append(title, collapse);

    // search-row (in the editor): query YouTube and add results to the queue.
    // Hidden entirely when no API key is configured.
    this.searchInput.setAttribute('type', 'text');
    onLang(() => this.searchInput.setAttribute('placeholder', t('music.searchPlaceholder')));
    const searchBtn = el('button');
    tText(searchBtn, 'music.searchBtn');
    searchBtn.onclick = () => void this.runSearch();
    this.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') void this.runSearch();
    });
    const searchRow = el('div', 'music-row');
    searchRow.append(this.searchInput, searchBtn);
    if (!this.apiKey) {
      searchRow.style.display = 'none';
      this.searchResults.style.display = 'none';
    }

    // transport: prev / play-pause toggle / next + volume
    const prevBtn = el('button');
    tText(prevBtn, 'music.prev');
    prevBtn.onclick = () => this.step(-1);
    this.ppBtn.onclick = () => (this.playing ? this.onPauseClick() : this.onPlayClick());
    const nextBtn = el('button');
    tText(nextBtn, 'music.next');
    nextBtn.onclick = () => this.step(1);
    this.transport.append(prevBtn, this.ppBtn, nextBtn);
    this.updatePlayPause();

    this.volume.type = 'range';
    this.volume.min = '0';
    this.volume.max = '100';
    this.volume.value = String(this.vol);
    this.volume.oninput = () => this.onVolumeInput(Number(this.volume.value));
    const volRow = el('div', 'music-row');
    const volLabel = el('span', 'music-vol-label');
    tText(volLabel, 'music.volume');
    volRow.append(volLabel, this.volume);

    tText(this.status, 'music.intro');
    // editor (expanded-only): search, volume, the queue, status.
    // The room auto-syncs on every change, so there's no manual sync button.
    this.editor.append(searchRow, this.searchResults, volRow, this.list, this.status);

    // transport (minimized-only) + now-playing; the embed lives in the editor so
    // it collapses away with the rest of the expanded view.
    this.panel.append(header, this.transport, this.nowPlaying, this.embed, this.editor);
    onLang(() => this.resizeGrip.title = t('music.resize'));
    this.panel.appendChild(this.resizeGrip);
    root.appendChild(this.panel);

    this.makeDraggable(header);
    this.makeResizable(this.resizeGrip);
    this.setOnline(false);
    this.applyCollapse();
    this.render();
    this.loadApi();
  }

  /** Reflect the collapsed/expanded state: minimized shows the transport bar;
   * expanded shows the video + editor and hides the transport. */
  private applyCollapse(): void {
    this.editor.style.display = this.open ? 'flex' : 'none';
    this.transport.style.display = this.open ? 'none' : 'flex';
    // the video only exists once a player is created; hide it while minimized
    this.embed.style.display = this.open && this.player ? '' : 'none';
  }

  /** Drag the whole panel around by its header (ignores clicks on the buttons). */
  private makeDraggable(handle: HTMLElement): void {
    handle.style.cursor = 'move';
    let dragging = false;
    let startX = 0;
    let startY = 0;
    let baseLeft = 0;
    let baseTop = 0;
    handle.addEventListener('pointerdown', (e) => {
      if ((e.target as HTMLElement).closest('button')) return; // not when grabbing a button
      const rect = this.panel.getBoundingClientRect();
      this.panel.style.left = `${rect.left}px`;
      this.panel.style.top = `${rect.top}px`;
      // release the CSS bottom/right anchors — otherwise pinning `top` too keeps
      // the panel stretched between both edges instead of moving it
      this.panel.style.bottom = 'auto';
      this.panel.style.right = 'auto';
      this.panel.style.transform = 'none'; // drop the centring transform once moved
      dragging = true;
      startX = e.clientX;
      startY = e.clientY;
      baseLeft = rect.left;
      baseTop = rect.top;
      handle.setPointerCapture(e.pointerId);
      e.preventDefault();
    });
    handle.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const w = this.panel.offsetWidth;
      const h = this.panel.offsetHeight;
      const nx = Math.max(0, Math.min(window.innerWidth - w, baseLeft + (e.clientX - startX)));
      const ny = Math.max(0, Math.min(window.innerHeight - h, baseTop + (e.clientY - startY)));
      this.panel.style.left = `${nx}px`;
      this.panel.style.top = `${ny}px`;
    });
    const end = (e: PointerEvent) => {
      dragging = false;
      try {
        handle.releasePointerCapture(e.pointerId);
      } catch {
        /* pointer already released */
      }
    };
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  }

  /** Resize the panel by dragging its bottom-right grip (anchors top-left). */
  private makeResizable(handle: HTMLElement): void {
    const MIN_W = 220;
    const MIN_H = 110;
    let resizing = false;
    let startX = 0;
    let startY = 0;
    let baseW = 0;
    let baseH = 0;
    handle.addEventListener('pointerdown', (e) => {
      const rect = this.panel.getBoundingClientRect();
      // pin to top-left so growing doesn't fight the bottom/right CSS anchors
      this.panel.style.left = `${rect.left}px`;
      this.panel.style.top = `${rect.top}px`;
      this.panel.style.bottom = 'auto';
      this.panel.style.right = 'auto';
      this.panel.style.transform = 'none';
      resizing = true;
      startX = e.clientX;
      startY = e.clientY;
      baseW = rect.width;
      baseH = rect.height;
      handle.setPointerCapture(e.pointerId);
      e.preventDefault();
      e.stopPropagation();
    });
    handle.addEventListener('pointermove', (e) => {
      if (!resizing) return;
      const maxW = window.innerWidth - this.panel.offsetLeft - 4;
      const maxH = window.innerHeight - this.panel.offsetTop - 4;
      const w = Math.max(MIN_W, Math.min(maxW, baseW + (e.clientX - startX)));
      const h = Math.max(MIN_H, Math.min(maxH, baseH + (e.clientY - startY)));
      this.panel.style.width = `${w}px`;
      this.panel.style.height = `${h}px`;
    });
    const end = (e: PointerEvent) => {
      resizing = false;
      try {
        handle.releasePointerCapture(e.pointerId);
      } catch {
        /* pointer already released */
      }
    };
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  }

  /** Toggle online mode: the room-sync button only matters with a connected room. */
  setOnline(online: boolean): void {
    const wasOnline = this.online;
    this.online = online;
    this.syncBtn.style.display = online ? '' : 'none';
    // Entering a room: drop any solo queue/playback so the shared room state
    // starts from a clean slate (otherwise our local video keeps blasting,
    // unsynced, in a room that may have nothing queued yet).
    if (online && !wasOnline) this.reset();
  }

  /** Reset to the default queue and stop playback (e.g. on entering a room). */
  reset(): void {
    this.playlist = DEFAULT_PLAYLIST.slice(); // start every room with the prefilled jukebox
    this.index = -1;
    this.playing = false;
    this.suppress(1500); // don't echo the programmatic stop back to the room
    if (this.player && this.playerReady) {
      try {
        this.player.pauseVideo();
      } catch {
        /* player not ready */
      }
    }
    this.searchResults.textContent = '';
    this.searchInput.value = '';
    this.status.textContent = '';
    this.render();
  }

  // ---- YouTube IFrame API bootstrap ----

  private loadApi(): void {
    const start = () => {
      this.api = window.YT ?? null;
      this.flush();
    };
    if (window.YT && window.YT.Player) {
      start();
      return;
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      start();
    };
    if (!document.querySelector(`script[src="${IFRAME_API_SRC}"]`)) {
      const script = el('script');
      script.src = IFRAME_API_SRC;
      document.head.appendChild(script);
    }
  }

  private flush(): void {
    const q = this.queued;
    this.queued = [];
    for (const fn of q) fn();
  }

  /** Run `fn` once a player exists, creating it on first use. */
  private withPlayer(fn: (p: YTPlayer) => void): void {
    if (this.player && this.playerReady) {
      fn(this.player);
      return;
    }
    this.queued.push(() => this.withPlayer(fn));
    if (!this.player && this.api) {
      // YT.Player REPLACES the element it's given with the iframe, so create the
      // player on a throwaway child and keep `embed` as the visible wrapper.
      // Only reveal it if currently expanded (minimized hides the video).
      if (this.open) this.embed.style.display = '';
      const target = document.createElement('div');
      this.embed.appendChild(target);
      this.player = new this.api.Player(target, {
        width: '100%',
        height: 150,
        playerVars: { playsinline: 1, rel: 0, modestbranding: 1 },
        events: {
          onReady: () => {
            this.playerReady = true;
            this.player?.setVolume(this.vol);
            this.flush();
            this.startMonitor();
          },
          onStateChange: (e) => this.onStateChange(e.data),
        },
      });
    }
  }

  private onStateChange(stateData: number): void {
    if (!this.api) return;
    const S = this.api.PlayerState;
    const wasPlaying = this.playing;
    this.playing = stateData === S.PLAYING;
    this.updatePlayPause();
    if (stateData === S.ENDED) {
      // auto-advance locally (each client advances on its own end → no broadcast storm)
      if (this.index < this.playlist.length - 1) this.doSelect(this.index + 1, 0, true);
      return;
    }
    if (stateData === S.PLAYING) {
      // re-baseline the seek detector whenever playback (re)starts (e.g. after buffering)
      this.lastTime = this.player?.getCurrentTime() ?? this.lastTime;
      this.lastTimeAt = Date.now();
      this.captureTitle();
      // play triggered from *inside* the iframe (not our transport / a remote apply)
      if (!wasPlaying && this.online && !this.suppressed()) {
        this.onAction({ action: 'play', index: this.index, positionMs: this.positionMs() });
      }
    } else if (stateData === S.PAUSED) {
      // pause triggered from inside the iframe — broadcast even if our cached
      // "playing" flag was stale (buffering can desync it), so it always syncs
      if (this.online && !this.suppressed()) this.onAction({ action: 'pause' });
    }
  }

  // ---- in-player change detection ----

  /** Mark the next ~`ms` as a programmatic change so the monitor/state events
   * don't re-broadcast it (prevents echo for our own transport + remote applies). */
  private suppress(ms = 1200): void {
    this.suppressUntil = Date.now() + ms;
  }

  private suppressed(): boolean {
    return Date.now() < this.suppressUntil;
  }

  /** Poll the live player for changes the IFrame API has no event for: scrubbing
   * the progress bar and dragging the volume slider inside the iframe. */
  private startMonitor(): void {
    if (this.monitorTimer != null) return;
    this.monitorTimer = window.setInterval(() => this.monitorTick(), 700);
  }

  private monitorTick(): void {
    const p = this.player;
    if (!p || !this.playerReady) return;
    const now = Date.now();
    const suppressed = this.suppressed();
    // volume changed inside the player → mirror it locally, broadcast if user-driven
    try {
      const v = Math.round(p.getVolume());
      if (v >= 0 && v !== this.vol) {
        this.vol = v;
        this.volume.value = String(v);
        if (this.online && !suppressed) this.onAction({ action: 'volume', volume: v });
      }
    } catch {
      /* player not ready for queries */
    }
    // seek detected as a jump between actual time and time we'd expect by now
    try {
      const tSec = p.getCurrentTime();
      const expected = this.playing ? this.lastTime + (now - this.lastTimeAt) / 1000 : this.lastTime;
      if (!suppressed && Math.abs(tSec - expected) > 1.5) {
        if (this.online) this.onAction({ action: 'seek', positionMs: Math.round(tSec * 1000) });
      }
      this.lastTime = tSec;
      this.lastTimeAt = now;
    } catch {
      /* player not ready for queries */
    }
  }

  /** Once the current video reports a title, store it and refresh the row. */
  private captureTitle(): void {
    const cur = this.playlist[this.index];
    if (!cur) return;
    const data = this.player?.getVideoData();
    if (data?.title && cur.title !== data.title) {
      cur.title = data.title;
      this.render();
    }
  }

  // ---- low-level player drivers (no broadcast) ----

  private doSelect(i: number, startSeconds: number, autoplay: boolean): void {
    if (i < 0 || i >= this.playlist.length) return;
    this.index = i;
    const id = this.playlist[i].id;
    this.suppress(2000); // loading + buffering can take a moment before PLAYING fires
    this.withPlayer((p) => {
      p.loadVideoById(id, startSeconds);
      if (!autoplay) p.pauseVideo();
    });
    this.render();
  }

  // ---- local UI handlers (broadcast via onAction) ----

  /** Add a store-unlocked track to the queue and play it immediately (called
   *  from the item store's "Play now" button on an owned 'music' item). */
  addOwnedTrack(id: string, title: string): void {
    this.enqueue(id, title);
    this.selectAndPlay(this.playlist.length - 1);
  }

  /** Append one track to the queue, broadcasting and auto-playing the first. */
  private enqueue(id: string, title: string): void {
    this.playlist.push({ id, title });
    this.status.textContent = t('music.added');
    this.render();
    this.broadcastPlaylist();
    // don't auto-play added tracks — just queue them (play by clicking the row)
    if (title === id) void this.fetchTitle(id); // only when we don't already have a name
  }

  // ---- YouTube Data API: search + playlist expansion (needs an API key) ----

  /** Search YouTube for the query box text and render clickable results — unless
   *  it's already a YouTube link (or bare video ID), in which case skip the
   *  search entirely and just queue that video. */
  private async runSearch(): Promise<void> {
    const q = this.searchInput.value.trim();
    if (!q) return;
    const directId = parseVideoId(q);
    if (directId) {
      this.enqueue(directId, directId); // title === id → enqueue() resolves the real title
      this.searchResults.textContent = '';
      this.searchInput.value = '';
      return;
    }
    if (!this.apiKey) {
      this.status.textContent = t('music.noKey');
      return;
    }
    this.status.textContent = t('music.searching');
    try {
      const url =
        `https://www.googleapis.com/youtube/v3/search?part=snippet&type=video&maxResults=10` +
        `&q=${encodeURIComponent(q)}&key=${this.apiKey}`;
      const r = await fetch(url);
      if (!r.ok) {
        this.status.textContent = t('music.searchFail');
        return;
      }
      const j = (await r.json()) as {
        items?: {
          id?: { videoId?: string };
          snippet?: {
            title?: string;
            thumbnails?: { default?: { url?: string }; medium?: { url?: string } };
          };
        }[];
      };
      const results: SearchResult[] = (j.items ?? [])
        .map((it) => ({
          id: it.id?.videoId ?? '',
          title: it.snippet?.title ?? '',
          thumb: it.snippet?.thumbnails?.medium?.url ?? it.snippet?.thumbnails?.default?.url,
        }))
        .filter((x) => x.id);
      this.renderResults(results);
      this.status.textContent = '';
    } catch {
      this.status.textContent = t('music.searchFail');
    }
  }

  private renderResults(results: SearchResult[]): void {
    this.searchResults.textContent = '';
    results.forEach((res) => {
      const row = el('div', 'music-result');
      const addIt = () => {
        this.enqueue(res.id, res.title || res.id);
        this.searchResults.textContent = '';
        this.searchInput.value = '';
      };
      if (res.thumb) {
        const thumb = el('img', 'music-thumb');
        thumb.src = res.thumb;
        thumb.alt = '';
        thumb.loading = 'lazy';
        thumb.onclick = addIt;
        row.append(thumb);
      }
      const label = el('span', 'music-item-title', res.title || res.id);
      label.title = res.title;
      const add = el('button', 'music-mini', '+');
      add.title = t('music.add');
      label.onclick = addIt;
      add.onclick = addIt;
      row.append(label, add);
      this.searchResults.appendChild(row);
    });
  }

  private selectAndPlay(i: number): void {
    this.doSelect(i, 0, true);
    this.onAction({ action: 'play', index: i, positionMs: 0 });
  }

  /** Reflect playing/paused on the single toggle button. */
  private updatePlayPause(): void {
    this.ppBtn.textContent = this.playing ? '⏸' : '▶';
    this.ppBtn.title = t(this.playing ? 'music.pause' : 'music.play');
  }

  private onPlayClick(): void {
    if (this.index < 0 && this.playlist.length) {
      this.selectAndPlay(0);
      return;
    }
    this.suppress();
    this.withPlayer((p) => p.playVideo());
    this.onAction({ action: 'play', index: this.index, positionMs: this.positionMs() });
  }

  private onPauseClick(): void {
    this.suppress();
    this.withPlayer((p) => p.pauseVideo());
    this.onAction({ action: 'pause' });
  }

  private step(delta: number): void {
    const i = this.index + delta;
    if (i >= 0 && i < this.playlist.length) this.selectAndPlay(i);
  }

  private onVolumeInput(v: number): void {
    this.vol = v;
    this.suppress();
    this.withPlayer((p) => p.setVolume(v));
    this.onAction({ action: 'volume', volume: v });
  }

  private removeAt(i: number): void {
    this.playlist.splice(i, 1);
    if (i < this.index) this.index--;
    else if (i === this.index) this.index = Math.min(this.index, this.playlist.length - 1);
    this.render();
    this.broadcastPlaylist();
  }

  private move(i: number, delta: number): void {
    const j = i + delta;
    if (j < 0 || j >= this.playlist.length) return;
    [this.playlist[i], this.playlist[j]] = [this.playlist[j], this.playlist[i]];
    if (this.index === i) this.index = j;
    else if (this.index === j) this.index = i;
    this.render();
    this.broadcastPlaylist();
  }

  /** Push the whole current state to a (re)joining player. */
  broadcastSync(): void {
    if (!this.playlist.length) return;
    this.onAction({
      action: 'sync',
      videoIds: this.playlist.map((t2) => t2.id),
      titles: this.playlist.map((t2) => t2.title),
      index: this.index,
      positionMs: this.positionMs(),
      volume: this.vol,
      paused: !this.playing,
    });
  }

  private onSyncClick(): void {
    this.status.textContent = t('music.synced');
    this.broadcastSync();
  }

  private broadcastPlaylist(): void {
    this.onAction({
      action: 'playlist',
      videoIds: this.playlist.map((tr) => tr.id),
      titles: this.playlist.map((tr) => tr.title),
      index: this.index,
    });
  }

  private positionMs(): number {
    try {
      return Math.round((this.player?.getCurrentTime() ?? 0) * 1000);
    } catch {
      return 0;
    }
  }

  /** Best-effort title lookup via YouTube oEmbed (no API key; ignores failures). */
  private async fetchTitle(id: string): Promise<void> {
    try {
      const r = await fetch(`https://www.youtube.com/oembed?format=json&url=https://www.youtube.com/watch?v=${id}`);
      if (!r.ok) return;
      const j = (await r.json()) as { title?: string };
      const track = this.playlist.find((tr) => tr.id === id);
      if (j.title && track && track.title !== j.title) {
        track.title = j.title;
        this.render();
        this.broadcastPlaylist();
      }
    } catch {
      /* CORS / offline — keep the ID as the label */
    }
  }

  // ---- remote application (never re-broadcasts) ----

  applyRemote(a: MusicAction): void {
    // every remote action drives the player programmatically — guard the
    // resulting state-change / time / volume jumps so we don't echo them back.
    this.suppress(a.action === 'playlist' || a.action === 'sync' ? 2000 : 1200);
    switch (a.action) {
      case 'playlist':
      case 'sync':
        if (a.videoIds) {
          this.playlist = a.videoIds.map((id, i) => ({ id, title: a.titles?.[i] ?? id }));
          if (typeof a.index === 'number') this.index = a.index;
          this.render();
        }
        if (a.action === 'sync') {
          if (typeof a.volume === 'number') {
            this.vol = a.volume;
            this.volume.value = String(a.volume);
            this.withPlayer((p) => p.setVolume(a.volume!));
          }
          if (typeof a.index === 'number') this.doSelect(a.index, (a.positionMs ?? 0) / 1000, !a.paused);
        }
        break;
      case 'play':
        if (typeof a.index === 'number') this.doSelect(a.index, (a.positionMs ?? 0) / 1000, true);
        else this.withPlayer((p) => p.playVideo());
        break;
      case 'pause':
        this.withPlayer((p) => p.pauseVideo());
        break;
      case 'seek':
        if (typeof a.positionMs === 'number') this.withPlayer((p) => p.seekTo(a.positionMs! / 1000, true));
        break;
      case 'volume':
        if (typeof a.volume === 'number') {
          this.vol = a.volume;
          this.volume.value = String(a.volume);
          this.withPlayer((p) => p.setVolume(a.volume!));
        }
        break;
    }
  }

  // ---- rendering ----

  private render(): void {
    this.syncBtn.onclick = () => this.onSyncClick();
    const cur = this.playlist[this.index];
    this.nowPlaying.textContent = cur ? cur.title : '';
    this.list.textContent = '';
    if (!this.playlist.length) {
      const empty = el('div', 'music-empty');
      empty.textContent = t('music.empty');
      this.list.appendChild(empty);
      return;
    }
    this.playlist.forEach((track, i) => {
      const row = el('div', 'music-item' + (i === this.index ? ' active' : ''));
      // drag to reorder
      row.draggable = true;
      row.ondragstart = (e) => {
        this.dragFrom = i;
        row.classList.add('dragging');
        if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
      };
      row.ondragend = () => {
        this.dragFrom = -1;
        this.list.querySelectorAll('.music-item').forEach((r) => r.classList.remove('dragging', 'dragover'));
      };
      row.ondragover = (e) => {
        e.preventDefault();
        if (this.dragFrom >= 0 && this.dragFrom !== i) row.classList.add('dragover');
      };
      row.ondragleave = () => row.classList.remove('dragover');
      row.ondrop = (e) => {
        e.preventDefault();
        row.classList.remove('dragover');
        if (this.dragFrom >= 0) this.reorder(this.dragFrom, i);
      };
      const handle = el('span', 'music-grip', '⠿');
      const label = el('span', 'music-item-title', `${i + 1}. ${track.title}`);
      label.onclick = () => this.selectAndPlay(i);
      const up = el('button', 'music-mini', '▲');
      up.title = t('music.up');
      up.onclick = () => this.move(i, -1);
      const down = el('button', 'music-mini', '▼');
      down.title = t('music.down');
      down.onclick = () => this.move(i, 1);
      const del = el('button', 'music-mini', '✕');
      del.title = t('music.delete');
      del.onclick = () => this.removeAt(i);
      for (const b of [up, down, del]) b.draggable = false;
      row.append(handle, label, up, down, del);
      this.list.appendChild(row);
    });
  }

  /** Move a queued track from one position to another (drag-and-drop reorder). */
  private reorder(from: number, to: number): void {
    if (from === to || from < 0 || to < 0 || from >= this.playlist.length || to >= this.playlist.length) return;
    const playing = this.index >= 0 ? this.playlist[this.index] : null;
    const [item] = this.playlist.splice(from, 1);
    this.playlist.splice(to, 0, item);
    if (playing) this.index = this.playlist.indexOf(playing); // keep pointing at the same track
    this.render();
    this.broadcastPlaylist();
  }
}
